import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ClassroomDeleteModal } from "adminComponents/organisms/ClassroomDeleteModal";
import { useErrorToast, useShowToast } from "adminComponents/utils/toast";
import { useDeleteClassroom as useDeleteClassroomMutation } from "lib/features/classrooms";
import { IClassroom } from "lib/types";

interface IResult {
  handleDeleteIntent: (classroom: IClassroom, accuracy?: number) => void;
  modal: React.ReactElement;
}

interface IArgs {
  onConfirm?: (classroom: IClassroom) => void;
  onSuccess?: () => void;
}

const useDeleteClassroom = ({
  onConfirm,
  onSuccess: _onSuccess,
}: IArgs): IResult => {
  const showToast = useShowToast();
  const { t } = useTranslation("admin", {
    useSuspense: false,
    keyPrefix: "classroomCommon.classroomDelete",
  });

  const [state, setState] = useState<{
    isModalOpen: boolean;
  }>({ isModalOpen: false });
  const [currentClassroom, setCurrentClassroom] = useState<{
    classroom: IClassroom;
    accuracy?: number;
  } | null>(null);

  const onSuccess = useCallback(() => {
    showToast(t("successToast"));
    setState({ isModalOpen: false });
    _onSuccess?.();
  }, [showToast, t, _onSuccess]);

  const deleteClassroom = useDeleteClassroomMutation({ onSuccess });

  useErrorToast(deleteClassroom.error);

  const handleDeleteIntent = useCallback(
    (classroom: IClassroom, accuracy?: number) => {
      setCurrentClassroom({
        classroom,
        accuracy,
      });

      setState({
        isModalOpen: true,
      });
    },
    []
  );

  const handleModalClose = useCallback(
    (isConfirmed?: boolean) => {
      if (!isConfirmed || !currentClassroom) {
        setState({ isModalOpen: false });
        return;
      }

      deleteClassroom.mutate({ id: currentClassroom.classroom.id });

      onConfirm?.(currentClassroom.classroom);
    },
    [currentClassroom, deleteClassroom, onConfirm]
  );

  const modal = useMemo(() => {
    if (!currentClassroom) return <></>;

    return (
      <ClassroomDeleteModal
        handleClose={handleModalClose}
        isLoading={deleteClassroom.isLoading}
        isOpen={state.isModalOpen}
        classroom={currentClassroom?.classroom}
        accuracy={currentClassroom?.accuracy}
      />
    );
  }, [
    deleteClassroom.isLoading,
    state.isModalOpen,
    handleModalClose,
    currentClassroom,
  ]);

  return useMemo(
    () => ({
      handleDeleteIntent,
      modal,
    }),
    [handleDeleteIntent, modal]
  );
};

export { useDeleteClassroom };
